import { Avatar } from '@repo/components/avatars';
import cn from 'classnames';
import { CheckIcon, LoaderIcon, XIcon } from '@repo/icons';
import { useWorkspaceContext } from '@repo/hooks';
import {
  Section,
  SettingsButton,
  SettingsSectionBox,
  SettingsSectionRow,
} from '@views/Settings/components';
import React, { useMemo, useRef, useState } from 'react';
import styles from './WorkspaceMembers.module.scss';
import { WorkspaceMember } from '@repo/utils/models/Workspace';
import { apiRequest } from '@repo/utils/helpers/api';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useDispatch } from 'react-redux';
import { alertUser } from '@repo/redux/actions/alerts';
import validEmail from '@repo/utils/helpers/valid-email';
import { Overlay, Tooltip } from '@repo/components/tooltips';
import openURL from '@repo/utils/helpers/open-url';
import { useAuthContext } from '@repo/hooks';
import { TeamsBadge } from '@repo/icons/ProductNameBadge';

type RemoveMemberAction = 'delete' | 'archive';

const confirmMemberRemoval = (
  member: WorkspaceMember,
  onConfirm: (action: RemoveMemberAction) => void,
) => {
  window.app.confirm(
    {
      title: 'Remove Workspace Member',
      message: `You are about to remove ${member.displayName} from this workspace. Please choose if you'd like to keep a copy of their data within this workspace or delete it.`,
      actions: [
        { title: 'Delete', variant: 'danger', value: 'delete' },
        { title: 'Keep Copy', variant: 'primary', value: 'archive', autoFocus: true },
      ],
    },
    (action?: RemoveMemberAction) => {
      if (action) {
        onConfirm(action);
      }
    },
  );
};

function MemberRow({ member, confirmMemberRemovals }) {
  const dispatch = useDispatch();
  const { userIsAdmin, activeWorkspace, updateActiveWorkspace } = useWorkspaceContext();

  const deleteMember = (member, action: RemoveMemberAction) => {
    updateActiveWorkspace({
      members: activeWorkspace?.members.filter(({ id }) => id !== member.id).toOrderedMap(),
    });
    apiRequest('DELETE', `/workspace/members/${member.id}`, { action }, { apiVersion: 'v2' })
      .then(() => {
        dispatch(alertUser(`Removed ${member.displayName} from workspace.`, 'success'));
      })
      .catch((err) => {
        dispatch(alertUser(`Failed to remove ${member.displayName} from workspace.`, 'error'));
        updateActiveWorkspace({ members: activeWorkspace?.members.set(member.id, member) });
        console.error(err);
      });
  };

  const onDeleteClick = () => {
    if (confirmMemberRemovals) {
      confirmMemberRemoval(member, deleteMember.bind(null, member));
    } else {
      deleteMember(member, 'delete');
    }
  };

  let memberLabel = <span className="information">Member</span>;
  if (userIsAdmin) {
    memberLabel = (
      <button onClick={onDeleteClick} className="btn">
        <XIcon width={12} height={12} hover hoverOpacity />
      </button>
    );
  }
  if (!member.isPersisted) {
    memberLabel = <LoaderIcon />;
  }

  return (
    <SettingsSectionRow className="row">
      <Avatar
        url={member.avatarURL}
        initials={member.displayName[0]}
        size="sm"
        name={member.displayName}
      />
      {member.displayName}
      {member.isAdmin ? <span className="information">Admin</span> : memberLabel}
    </SettingsSectionRow>
  );
}

const UpgradeMessage = () => {
  const { user } = useAuthContext();

  return (
    <Section className={styles.container}>
      <SettingsSectionBox className="upgradeMessage">
        <span className="upgradeTitle">
          Upgrade to Clay <TeamsBadge />
        </span>
        <SettingsButton onClick={() => openURL(user.upgrade_url)} className={'upgradeButton'}>
          Upgrade
        </SettingsButton>
        <p>The Teams Plan allows you to add unlimited members to your workspace.</p>
      </SettingsSectionBox>
    </Section>
  );
};

export interface WorkspaceMembersSettingsPanelProps {
  className?: string;
  optionsClassName?: string;
  canRenderUpgradeMsg?: boolean;
  confirmMemberRemovals?: boolean;
  showDisabledAddMemberBtn?: boolean;
}

export function WorkspaceMemberManager({
  className,
  optionsClassName,
  canRenderUpgradeMsg = true,
  confirmMemberRemovals = true,
  showDisabledAddMemberBtn = false,
}: WorkspaceMembersSettingsPanelProps) {
  const dispatch = useDispatch();
  const { activeWorkspace, updateActiveWorkspace, userIsAdmin } = useWorkspaceContext();
  const { getEntitlementValue } = useAuthContext();
  const addMemberRowRef = useRef(null);
  const [inputingNewMemberEmail, setInputingNewMemberEmail] = useState(false);
  const [savingNewMember, setSavingNewMember] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const validNewMemberEmail = validEmail(newMemberEmail);
  const isAtMemberLimit = useMemo(() => {
    const limit = getEntitlementValue('additional.team.members') as number;
    // unlimited
    if (limit === null) {
      return false;
    }
    // Admin counts as one, whereas the entitlement specifies "additional" members beyond the admin
    return (activeWorkspace?.members?.size || 1) - 1 >= limit;
  }, [activeWorkspace?.members]);

  const addMember = () => {
    if (!validNewMemberEmail || savingNewMember) {
      return;
    }

    const currentMembers = activeWorkspace?.members;
    const placeholderMember = new WorkspaceMember({ email: newMemberEmail });
    const membersWithPlaceholder = currentMembers?.set(placeholderMember.id, placeholderMember);

    setSavingNewMember(true);
    setInputingNewMemberEmail(false);
    setTimeout(() => updateActiveWorkspace({ members: membersWithPlaceholder }), 0);
    apiRequest<{
      id: number;
      email: string;
      first_name: string;
      last_name: string;
    }>('POST', '/workspace/members/', { email: newMemberEmail }, { apiVersion: 'v2' })
      .then((newMemberData) => {
        updateActiveWorkspace({
          members: membersWithPlaceholder.update(placeholderMember.id, (member) =>
            member.copyWith(newMemberData),
          ),
        });
      })
      .catch((err) => {
        console.error('failed to create workspace member', err);
        updateActiveWorkspace({ members: currentMembers });
        dispatch(alertUser(`Failed to add ${newMemberEmail} to workspace.`, 'error'));
      })
      .finally(() => {
        setSavingNewMember(false);
        setNewMemberEmail('');
      });
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && validNewMemberEmail) {
      addMember();
    }

    if (e.key === 'Escape' && newMemberEmail.length !== 0) {
      e.stopPropagation();
      setNewMemberEmail('');
    }

    if (e.key === 'Escape' && newMemberEmail.length === 0) {
      e.stopPropagation();
      setInputingNewMemberEmail(false);
    }
  };

  const addMoreBtnDisabled =
    !userIsAdmin || isAtMemberLimit || inputingNewMemberEmail || savingNewMember;

  return (
    <>
      <Section className={cn(styles.container, className)}>
        <SettingsSectionBox className={cn(optionsClassName)}>
          <TransitionGroup>
            {activeWorkspace?.members
              .entrySeq()
              .toArray()
              .map(([key, member]) => (
                <CSSTransition
                  key={key}
                  timeout={member.isPersisted ? 300 : 0}
                  classNames={{
                    exit: 'exit',
                    exitActive: 'exitActive',
                  }}
                >
                  <MemberRow member={member} confirmMemberRemovals={confirmMemberRemovals} />
                </CSSTransition>
              ))}
          </TransitionGroup>
          <CSSTransition
            in={inputingNewMemberEmail}
            nodeRef={addMemberRowRef}
            timeout={savingNewMember ? 0 : 50}
            classNames={{
              exit: 'exit',
              exitActive: 'exitActive',
            }}
            unmountOnExit
          >
            <SettingsSectionRow ref={addMemberRowRef} className="addMemberRow">
              <input
                type="email"
                className="emailInput"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder="Enter member email address"
                autoFocus
              />
              <div className="actions">
                <Tooltip
                  placement="top"
                  overlay={
                    <Overlay className={cn(validNewMemberEmail && 'hidden')}>
                      Please enter a valid email address
                    </Overlay>
                  }
                >
                  <span>
                    <button
                      onClick={addMember}
                      className={cn('btn', 'submitBtn')}
                      disabled={!validNewMemberEmail}
                    >
                      <CheckIcon width={12} height={12} />
                    </button>
                  </span>
                </Tooltip>
                <button onClick={() => setInputingNewMemberEmail(false)} className={cn('btn')}>
                  <XIcon width={12} height={12} hover hoverOpacity />
                </button>
              </div>
            </SettingsSectionRow>
          </CSSTransition>
        </SettingsSectionBox>
        {(showDisabledAddMemberBtn || !addMoreBtnDisabled) && (
          <SettingsButton
            className="addMemberBtn"
            onClick={() => setInputingNewMemberEmail(true)}
            alwaysRender={showDisabledAddMemberBtn}
            disabled={addMoreBtnDisabled}
          >
            Add Member
          </SettingsButton>
        )}
      </Section>
      {canRenderUpgradeMsg && isAtMemberLimit && userIsAdmin && <UpgradeMessage />}
    </>
  );
}

export default function WorkspaceMemberSettingsPanel() {
  return (
    <WorkspaceMemberManager canRenderUpgradeMsg={!window.desktopIntegrations?.appInfo.isMAS} />
  );
}
